import { useEffect, useMemo, useRef, useState } from 'react'
import {
  isKnownUsState,
  normalizeStateCode
} from '@/features/enrollment/components/eligibilitySteps/states'
import { useEnrollmentStore } from '@/stores'
import {
  getEnrollmentFieldEditability,
  getMostRelevantEnrollment
} from '../../../utils/enrollmentHydration'
import { getEnrollmentString } from '../../../utils/enrollmentHydration/helpers'
import type { ApplicationFormData, ApplicationFormState, FieldErrors } from '../types'
import { getInitialApplicationViewState } from '../utils/state'
import {
  buildAddressDisplayValue,
  mergeApplicationFormDataWithProfile
} from '../utils/storage'

function deriveAddressStateRestriction(trainingLocationState: string) {
  const normalizedStateCode = normalizeStateCode(trainingLocationState)
  return isKnownUsState(normalizedStateCode) ? normalizedStateCode : ''
}

function isSameApplicationFormData(
  previous: ApplicationFormData,
  next: ApplicationFormData
) {
  return (
    previous.firstName === next.firstName &&
    previous.lastName === next.lastName &&
    previous.middleName === next.middleName &&
    previous.phoneNumber === next.phoneNumber &&
    previous.email === next.email &&
    previous.addressLine1 === next.addressLine1 &&
    previous.city === next.city &&
    previous.state === next.state &&
    previous.zipCode === next.zipCode &&
    previous.birthDate === next.birthDate &&
    previous.about === next.about &&
    previous.termsAccepted === next.termsAccepted &&
    previous.smsConsent === next.smsConsent &&
    previous.marketingConsent === next.marketingConsent
  )
}

export function useApplicationFormState(): ApplicationFormState {
  const profile = useEnrollmentStore(state => state.userProfile)
  const sessionEnrollment = useEnrollmentStore(state => state.resumedEnrollment)
  const updateEnrollmentDraft = useEnrollmentStore(state => state.updateFormData)
  const fieldEditability = useMemo(
    () =>
      getEnrollmentFieldEditability(sessionEnrollment ?? getMostRelevantEnrollment(profile)),
    [profile, sessionEnrollment]
  )
  const addressStateRestriction = useMemo(
    () =>
      deriveAddressStateRestriction(
        getEnrollmentString(sessionEnrollment, ['trainingLocationState'])
      ),
    [sessionEnrollment]
  )
  const initialStateRef = useRef<{ formData: ApplicationFormData; fullAddress: string } | null>(null)

  if (initialStateRef.current === null) {
    initialStateRef.current = getInitialApplicationViewState()
  }

  const initialState = initialStateRef.current
  const [formData, setFormData] = useState(initialState.formData)
  const [fullAddress, setFullAddress] = useState(initialState.fullAddress)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const isSmsConsentLocked = fieldEditability.smsConsent === false

  useEffect(() => {
    if (!profile) {
      return
    }

    setFormData(previous => {
      const nextFormData = mergeApplicationFormDataWithProfile(previous, profile)
      return isSameApplicationFormData(previous, nextFormData) ? previous : nextFormData
    })

    setFullAddress(previous =>
      previous || buildAddressDisplayValue(mergeApplicationFormDataWithProfile(formData, profile))
    )
  }, [profile])

  useEffect(() => {
    if (!isSmsConsentLocked) {
      return
    }

    setFormData(previous => {
      if (previous.termsAccepted === previous.smsConsent) {
        return previous
      }

      return {
        ...previous,
        termsAccepted: previous.smsConsent
      }
    })
  }, [isSmsConsentLocked])

  useEffect(() => {
    updateEnrollmentDraft({
      firstName: formData.firstName,
      lastName: formData.lastName,
      middleName: formData.middleName,
      email: formData.email,
      phone: formData.phoneNumber,
      phoneNumber: formData.phoneNumber,
      addressLine1: formData.addressLine1,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      birthDate: formData.birthDate,
      about: formData.about,
      termsAccepted: formData.termsAccepted,
      marketingConsent: formData.marketingConsent,
      smsConsent: formData.smsConsent
    })
  }, [formData, updateEnrollmentDraft])

  return {
    formData,
    addressStateRestriction,
    fieldEditability,
    setFormData,
    fullAddress,
    setFullAddress,
    isSubmitting,
    setIsSubmitting,
    submitError,
    setSubmitError,
    fieldErrors,
    setFieldErrors
  }
}
